import { setIn } from "formik";
import { assessmentScorePillars } from "../../../../../form/components/integrity-scores/scoreCategories";
import type { FormikErrors } from "formik";
import { ValidationError } from "yup";
import { editSolutionFormSchema } from "../../../../../edit/form/schema";
import type { EditSolutionFormValues } from "../../../../../edit/form/types";
import type { AssessmentReportSectionId } from "../../../types";

const sectionSchemaPaths: Record<
  AssessmentReportSectionId,
  ReadonlyArray<string>
> = {
  text: [
    "solution-short-description",
    "assessmentReport.sdgInfo",
    "assessmentReport.developerInfo",
    "assessmentReport.monitoringInfo",
    "assessmentReport.locationInfo",
  ],
  images: ["location-pin-map-link", "satellite-imagery"],
  data: [
    "solution-sdg",
    "pillars.carbon.scores.total",
    "pillars.carbon.benefits.quantity",
    "pillars.carbon.benefits.description",
    "pillars.biodiversity.scores.total",
    "pillars.biodiversity.benefits.quantity",
    "pillars.biodiversity.benefits.description",
    "pillars.people.scores.total",
    "pillars.people.benefits.quantity",
    "pillars.people.benefits.description",
  ],
};

const addError = (
  errors: FormikErrors<EditSolutionFormValues>,
  path: string,
  message: string | undefined,
) => (message ? setIn(errors, path, message) : errors);

const addSchemaPathErrors = (
  errors: FormikErrors<EditSolutionFormValues>,
  path: string,
  values: EditSolutionFormValues,
): FormikErrors<EditSolutionFormValues> => {
  try {
    editSolutionFormSchema.validateSyncAt(path, values, { abortEarly: false });
    return errors;
  } catch (error) {
    if (!(error instanceof ValidationError)) return errors;

    const validationErrors = error.inner.length > 0 ? error.inner : [error];
    return validationErrors.reduce(
      (nextErrors, validationError) =>
        addError(
          nextErrors,
          validationError.path ?? path,
          validationError.message,
        ),
      errors,
    );
  }
};

export function validateAssessmentReportSection(
  section: AssessmentReportSectionId,
  values: EditSolutionFormValues,
): FormikErrors<EditSolutionFormValues> {
  let errors: FormikErrors<EditSolutionFormValues> = {};

  for (const path of sectionSchemaPaths[section] ?? []) {
    errors = addSchemaPathErrors(errors, path, values);
  }

  if (section === "data") {
    for (const pillar of assessmentScorePillars) {
      for (const category of pillar.categories) {
        const path = `pillars.${pillar.key}.scores.${category.key}`;
        errors = addSchemaPathErrors(errors, `${path}.score`, values);
        errors = addSchemaPathErrors(errors, `${path}.explanation`, values);
        errors = addSchemaPathErrors(errors, `${path}.status`, values);
      }
    }
  }

  return errors;
}
